/**
 * File Upload Hook
 *
 * Handles PDF file upload and text paste for template build.
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@store/hooks';

import {
  useUploadPdfForTemplateBuildMutation,
  useUploadTextForTemplateBuildMutation,
} from '../store/api';
import {
  initializeTemplateBuild,
  setBuildStatus,
  setError,
  setTaskId,
  setThreadId,
} from '../store/chatSlice';

/**
 * Hook for handling file upload and text paste for template build
 *
 * @returns Upload functions and state
 */
export const useFileUpload = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [uploadPdf, { isLoading: isUploadingPdf }] = useUploadPdfForTemplateBuildMutation();
  const [uploadText, { isLoading: isUploadingText }] = useUploadTextForTemplateBuildMutation();

  const [error, setUploadError] = useState<string | null>(null);

  /**
   * Upload a PDF file to start template build
   */
  const uploadFile = useCallback(
    async (file: File) => {
      setUploadError(null);

      // Validate file type
      if (file.type !== 'application/pdf') {
        const errorMsg = 'Please upload a PDF file';
        setUploadError(errorMsg);
        return null;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const errorMsg = 'File size must be less than 10MB';
        setUploadError(errorMsg);
        return null;
      }

      try {
        dispatch(setBuildStatus('uploading'));
        
        const result = await uploadPdf({ file }).unwrap();

        // Initialize build state with task ID
        dispatch(initializeTemplateBuild({
          taskId: result.task_id,
          threadId: result.thread_id,
        }));
        dispatch(setTaskId(result.task_id));
        dispatch(setThreadId(result.thread_id));
        dispatch(setBuildStatus('processing'));

        // Navigate to the build page with task ID
        navigate(`/templates/build/${result.task_id}`);

        return result;
      } catch (err: unknown) {
        const error = err as { data?: { message?: string }; message?: string };
        const errorMsg = error?.data?.message || error?.message || 'Failed to upload file';
        setUploadError(errorMsg);
        dispatch(setError(errorMsg));
        dispatch(setBuildStatus('failed'));
        return null;
      }
    },
    [dispatch, navigate, uploadPdf],
  );

  /**
   * Upload pasted text to start template build
   */
  const uploadPastedText = useCallback(
    async (text: string, filename?: string) => {
      setUploadError(null);

      // Validate text content
      if (!text.trim()) {
        const errorMsg = 'Please enter some text content';
        setUploadError(errorMsg);
        return null;
      }

      // Validate text length (min 50 characters)
      if (text.trim().length < 50) {
        const errorMsg = 'Please enter at least 50 characters of content';
        setUploadError(errorMsg);
        return null;
      }

      try {
        dispatch(setBuildStatus('uploading'));

        const result = await uploadText({ text, filename }).unwrap();

        // Initialize build state with task ID
        dispatch(initializeTemplateBuild({
          taskId: result.task_id,
          threadId: result.thread_id,
        }));
        dispatch(setTaskId(result.task_id));
        dispatch(setThreadId(result.thread_id));
        dispatch(setBuildStatus('processing'));

        // Navigate to the build page with task ID
        navigate(`/templates/build/${result.task_id}`);

        return result;
      } catch (err: unknown) {
        const error = err as { data?: { message?: string }; message?: string };
        const errorMsg = error?.data?.message || error?.message || 'Failed to upload text';
        setUploadError(errorMsg);
        dispatch(setError(errorMsg));
        dispatch(setBuildStatus('failed'));
        return null;
      }
    },
    [dispatch, navigate, uploadText],
  );

  /**
   * Clear the upload error
   */
  const clearError = useCallback(() => {
    setUploadError(null);
  }, []);

  return {
    uploadFile,
    uploadPastedText,
    isUploading: isUploadingPdf || isUploadingText,
    isUploadingPdf,
    isUploadingText,
    error,
    clearError,
  };
};

